/**
 * Sockeloen Product Configurator — Alpine.js Component
 * Reads product variants + metafields via embedded JSON, drives step-by-step UI.
 */
function sckConfigurator(configJSON) {
  return {
    // --- Config from Liquid ---
    config: configJSON,

    // --- State ---
    currentStep: 0,
    selections: {},
    selectedVariantId: null,
    selectedVariantPrice: 0,
    selectedQty: 0,
    designOption: null, // 'own_design' | 'we_design' | 'no_design'
    modalOpen: false,
    modalContent: '',
    modalTitle: '',
    loading: false,
    addedToCart: false,
    activeGalleryImage: 0,

    // --- Computed ---
    get steps() {
      return (this.config.steps || []).filter(function(s) {
        return !(this.config.hidden_steps || []).includes(s.key);
      }.bind(this));
    },

    get totalSteps() {
      // steps + 1 for the built-in quantity step
      return this.steps.length + 1;
    },

    get progressPercent() {
      return ((this.currentStep + 1) / this.totalSteps) * 100;
    },

    get isQuantityStep() {
      return this.currentStep >= this.steps.length;
    },

    get currentStepData() {
      if (this.isQuantityStep) return null;
      return this.steps[this.currentStep] || null;
    },

    get canGoNext() {
      if (this.isQuantityStep) return false; // quantity step has CTA instead
      var step = this.currentStepData;
      if (!step) return false;
      if (step.key === 'design_option') {
        return !!this.designOption;
      }
      if (step.required) {
        return !!this.selections[step.key];
      }
      return true;
    },

    get canGoBack() {
      return this.currentStep > 0;
    },

    get perPairPrice() {
      if (!this.selectedVariantPrice || !this.selectedQty) return 0;
      return this.selectedVariantPrice / this.selectedQty;
    },

    get totalPrice() {
      return this.selectedVariantPrice || 0;
    },

    get hasDesignFee() {
      return this.designOption === 'we_design';
    },

    get grandTotal() {
      var total = this.totalPrice;
      if (this.hasDesignFee) {
        total += (this.config.design_fee || 50) * 100; // design_fee is in EUR, variant prices in cents
      }
      return total;
    },

    get deliveryDate() {
      var days = this.config.delivery_days || 21;
      var date = new Date();
      var added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        var dow = date.getDay();
        if (dow !== 0 && dow !== 6) added++;
      }
      return date;
    },

    get deliveryDateFormatted() {
      var d = this.deliveryDate;
      var months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      return months[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
    },

    get urgencyDate() {
      var tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      // skip weekends
      if (tomorrow.getDay() === 0) tomorrow.setDate(tomorrow.getDate() + 1);
      if (tomorrow.getDay() === 6) tomorrow.setDate(tomorrow.getDate() + 2);
      var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return months[tomorrow.getMonth()] + ' ' + tomorrow.getDate();
    },

    get cutoffTime() {
      return this.config.cutoff_time || '11:30';
    },

    get selectionsSummary() {
      var items = [];
      var self = this;
      this.steps.forEach(function(step) {
        if (step.key === 'design_option') {
          if (self.designOption) {
            var labels = {
              'own_design': 'Upload own design',
              'we_design': 'We design for you',
              'no_design': 'No design yet'
            };
            items.push({ label: step.label, value: labels[self.designOption] || self.designOption });
          }
        } else if (self.selections[step.key]) {
          var option = (step.options || []).find(function(o) { return o.id === self.selections[step.key]; });
          items.push({ label: step.label, value: option ? option.label : self.selections[step.key] });
        }
      });
      if (self.selectedQty) {
        items.push({ label: 'Quantity', value: self.selectedQty + ' pairs' });
      }
      return items;
    },

    get ctaLabel() {
      if (this.designOption === 'we_design') return 'Order with our design service';
      if (this.designOption === 'no_design') return 'Order — Upload design later';
      return 'Order with your own design';
    },

    get canAddToCart() {
      return this.selectedVariantId && !this.loading;
    },

    get variants() {
      return this.config.variants || [];
    },

    get basePerPairPrice() {
      // First variant's per-pair price for discount calculation
      var v = this.variants;
      if (!v.length) return 0;
      var first = v[0];
      if (!first.price || !first.qty) return 0;
      return first.price / first.qty;
    },

    // --- Methods ---
    init() {
      // Pre-select defaults if set
      var self = this;
      this.steps.forEach(function(step) {
        if (step.default_option) {
          if (step.key === 'design_option') {
            self.designOption = step.default_option;
          } else {
            self.selections[step.key] = step.default_option;
          }
        }
      });
    },

    nextStep() {
      if (this.currentStep < this.totalSteps - 1) {
        this.currentStep++;
      }
    },

    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },

    goToStep(index) {
      if (index >= 0 && index < this.totalSteps) {
        this.currentStep = index;
      }
    },

    selectOption(stepKey, optionId) {
      this.selections[stepKey] = optionId;
    },

    selectDesignOption(option) {
      this.designOption = option;
    },

    selectVariant(variant) {
      if (variant.on_request) return;
      this.selectedVariantId = variant.id;
      this.selectedVariantPrice = variant.price;
      this.selectedQty = variant.qty;
    },

    isOptionSelected(stepKey, optionId) {
      return this.selections[stepKey] === optionId;
    },

    isVariantSelected(variantId) {
      return this.selectedVariantId === variantId;
    },

    openModal(title, content) {
      this.modalTitle = title;
      this.modalContent = content;
      this.modalOpen = true;
      document.body.style.overflow = 'hidden';
    },

    closeModal() {
      this.modalOpen = false;
      this.modalTitle = '';
      this.modalContent = '';
      document.body.style.overflow = '';
    },

    formatPrice(cents) {
      if (!cents && cents !== 0) return '—';
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    },

    formatPricePerPair(cents) {
      if (!cents && cents !== 0) return '—';
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    },

    discountPercent(variant) {
      if (!variant.price || !variant.qty || variant.on_request) return 0;
      var perPair = variant.price / variant.qty;
      var base = this.basePerPairPrice;
      if (!base || perPair >= base) return 0;
      return Math.round(((base - perPair) / base) * 100);
    },

    setGalleryImage(index) {
      this.activeGalleryImage = index;
    },

    scrollToAccordion(id) {
      var el = document.getElementById(id);
      if (el) {
        el.setAttribute('open', '');
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },

    async addToCart() {
      if (!this.canAddToCart) return;

      this.loading = true;
      this.addedToCart = false;

      try {
        // Build line item properties from selections
        var properties = {};
        var self = this;
        this.steps.forEach(function(step) {
          if (step.key === 'design_option') {
            var labels = {
              'own_design': 'Upload own design',
              'we_design': 'We design for you (+€50)',
              'no_design': 'No design yet'
            };
            if (self.designOption) {
              properties['Design'] = labels[self.designOption] || self.designOption;
            }
          } else if (self.selections[step.key]) {
            var option = (step.options || []).find(function(o) { return o.id === self.selections[step.key]; });
            properties[step.label] = option ? option.label : self.selections[step.key];
          }
        });

        // 1. Add the sock product variant
        var formData = new FormData();
        formData.append('id', this.selectedVariantId);
        formData.append('quantity', 1);
        Object.keys(properties).forEach(function(key) {
          formData.append('properties[' + key + ']', properties[key]);
        });

        var fetchOpts = {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        };

        var response = await fetch('/cart/add.js', fetchOpts);
        var result = await response.json();

        if (result.status) {
          throw new Error(result.description || 'Error adding to cart');
        }

        // 2. If "we design" selected, add design service product
        if (this.hasDesignFee && this.config.design_service_variant_id) {
          var designFormData = new FormData();
          designFormData.append('id', this.config.design_service_variant_id);
          designFormData.append('quantity', 1);
          designFormData.append('properties[For]', this.config.product_title || 'Custom Socks');

          var designResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: designFormData
          });
          var designResult = await designResponse.json();
          if (designResult.status) {
            console.warn('Design service add failed:', designResult.description);
          }
        }

        // 3. Redirect to cart
        this.addedToCart = true;
        window.location.href = '/cart';

      } catch (error) {
        console.error('Add to cart error:', error);
        alert('Could not add to cart. Please try again.');
      } finally {
        this.loading = false;
      }
    }
  };
}
